import { createClient } from "@supabase/supabase-js";

/* Both values are public by design. They identify the project; Row Level
   Security in Postgres decides what they can actually reach. The
   service_role key is never referenced here and must never reach the
   browser bundle. */
const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const isConfigured = Boolean(
  url && anonKey && !url.includes("your-project-ref")
);

export const supabase = isConfigured
  ? createClient(url, anonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    })
  : null;

/* Turns a Supabase error into something a project manager can act on
   rather than a raw Postgres string. */
export function friendlyError(error) {
  if (!error) return "";
  const msg = String(error.message || error);
  if (/invalid login credentials/i.test(msg)) {
    return "That email and password did not match. Check both and try again.";
  }
  if (/email not confirmed/i.test(msg)) {
    return "This account has not been confirmed yet. Check your inbox for the confirmation link.";
  }
  if (/signups not allowed|signup is disabled/i.test(msg)) {
    return "New accounts are invite only. Ask Heidi to send you an invitation.";
  }
  if (/rate limit|too many/i.test(msg)) {
    return "Too many attempts. Wait a minute and try again.";
  }
  /* A column or table the code expects and the database has not got. Always
     the same cause: a migration written but never run. Saying "column
     profiles.deactivated_at does not exist" sends a project manager looking
     for a bug in the app, when the fix is one file in the SQL editor. */
  if (/column .* does not exist|relation .* does not exist|schema cache/i.test(msg)) {
    const what = /column ["']?([\w.]+)["']? does not exist/i.exec(msg)?.[1]
      || /relation ["']?([\w.]+)["']? does not exist/i.exec(msg)?.[1];
    return "This part of the app needs a database update that has not been run yet"
      + (what ? ` (it is looking for ${what})` : "")
      + ". Run the newest UPDATE_ file in supabase/migrations in the Supabase SQL editor.";
  }
  /* Supabase says "Error sending recovery email" and stops there, which reads
     as though the person did something wrong. They did not: the address was
     accepted and the mail service refused to send. It is a settings problem
     and only Heidi can fix it, so the message says so rather than leaving
     somebody retrying a button that cannot work. */
  if (/error sending .*(email|mail)/i.test(msg)) {
    return "The email could not be sent. That is the mail settings at our end, not anything you did. Tell Heidi, and the sign-in itself still works normally.";
  }
  /* The link in the email has to come back to an address Supabase has been
     told about. Moving the site to a new domain without adding it to the list
     produces this, and the app looks broken when it is one setting. */
  if (/redirect|not allowed/i.test(msg)) {
    return "The link in that email would come back to an address this app has not been told to trust. Tell Heidi: the site's address needs adding to the sign-in settings.";
  }
  /* Supabase says "Bucket not found" and stops, which reads as though the file
     were wrong. It is not: the place files go has not been made yet, and only
     Heidi can make it. */
  if (/bucket not found/i.test(msg)) {
    return "There is nowhere to put files yet. The document store has not been created on this project. Tell Heidi: it is one bucket named contract-documents, made private, in Supabase under Storage.";
  }
  if (/exceeded the maximum allowed size|payload too large/i.test(msg)) {
    return "That file is too big for the store. Record the document without attaching it, and keep the lines you take from it, which is what the report actually uses.";
  }
  if (/row-level security|permission denied/i.test(msg)) {
    return "You do not have access to that contract.";
  }
  if (/fetch|network/i.test(msg)) {
    return "Could not reach the server. Check your connection and try again.";
  }
  return msg;
}
